import React from 'react'

import { Button, Icon, Refresh } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, string } from 'prop-types'

import { NOOP } from 'Utils/stableDefaults'

import PlanNotice from './PlanNotice'
import { SUBSCRIPTION_STATUS, isPaymentFailedStatus } from '../../../constants'

const NOTICES_KEY = 'subscription_management.state_notices'
const CONTACT_URL = '/contact-us'

function StateNotice({
  displayStatus,
  translate,
  pausedUntilDate = null,
  pauseScheduledDate = null,
  pauseResumesDate = null,
  accessEndsDate = null,
  endedOn = null,
  suspendedDate = null,
  termPriceLabel,
  updatePaymentUrl,
  resumeInProgress = false,
  isRetrySubmitted = false,
  canResume = false,
  isResumeLoading = false,
  onResume: handleResume = NOOP,
  canCancelScheduledPause = false,
  isCancelScheduledPauseLoading = false,
  canRetry = false,
  isRetryLoading = false,
  onRetry: handleRetry = NOOP,
  canceledByAdmin = false,
  canRestore = false,
  isRestoreLoading = false,
  onRenew: handleRenew = NOOP,
  onReactivate: handleReactivate = NOOP,
}) {
  const actionButton = (label, props) => (
    <Button type="secondary" size="small" actionType="button" customClass="plan-notice__action" {...props}>
      {label}
    </Button>
  )

  if (resumeInProgress) {
    return (
      <PlanNotice
        variant="neutral"
        title={translate('subscription_management.status_banners.resume_requested.title')}
        description={translate('subscription_management.status_banners.resume_requested.description')}
      />
    )
  }

  if (isRetrySubmitted) {
    return (
      <PlanNotice
        variant="neutral"
        title={translate('subscription_management.retry_payment.processing.title')}
        description={translate('subscription_management.retry_payment.processing.description')}
      />
    )
  }

  if (isPaymentFailedStatus(displayStatus)) {
    return (
      <PlanNotice
        variant="error"
        title={translate('subscription_management.payment_failed.title')}
        description={translate('subscription_management.payment_failed.description')}
        action={(
          <>
            {actionButton(translate('subscription_management.payment_failed.action'), { href: updatePaymentUrl })}
            {canRetry && actionButton(
              translate(`subscription_management.retry_payment.${isRetryLoading ? 'button_loading' : 'button'}`),
              {
                onClick: handleRetry,
                disabled: isRetryLoading,
                icon: <Icon svgComponent={Refresh} size="tiny" color="dark" />,
              }
            )}
          </>
        )}
      />
    )
  }

  switch (displayStatus) {
    case SUBSCRIPTION_STATUS.PAUSE_SCHEDULED:
      return (
        <PlanNotice
          variant="neutral"
          title={translate(`${NOTICES_KEY}.pause_scheduled.title`, { date: pauseScheduledDate })}
          description={pauseResumesDate
            ? translate(`${NOTICES_KEY}.pause_scheduled.description`, { date: pauseResumesDate })
            : translate('subscription_management.status_banners.pause_scheduled.description')}
          action={canCancelScheduledPause && actionButton(
            translate(`${NOTICES_KEY}.pause_scheduled.action`),
            { onClick: handleResume, disabled: isCancelScheduledPauseLoading }
          )}
        />
      )
    case SUBSCRIPTION_STATUS.PAUSED:
      return (
        <PlanNotice
          variant="neutral"
          title={translate('subscription_management.paused.notice_title', { date: pausedUntilDate })}
          description={translate('subscription_management.paused.notice_description', {
            date: pausedUntilDate,
            price: termPriceLabel,
          })}
          action={canResume && actionButton(
            translate('subscription_management.paused.resume_now'),
            { onClick: handleResume, disabled: isResumeLoading }
          )}
        />
      )
    case SUBSCRIPTION_STATUS.SUSPENDED:
      return (
        <PlanNotice
          variant="error"
          title={translate(`${NOTICES_KEY}.suspended.title`)}
          description={suspendedDate
            ? translate(`${NOTICES_KEY}.suspended.description`, { date: suspendedDate })
            : translate(`${NOTICES_KEY}.suspended.description_no_date`)}
          action={actionButton(translate(`${NOTICES_KEY}.suspended.action`), { href: CONTACT_URL })}
        />
      )
    case SUBSCRIPTION_STATUS.PENDING_CANCEL:
      if (canceledByAdmin) return null

      return (
        <PlanNotice
          variant="warning"
          title={translate(`${NOTICES_KEY}.pending_cancel.title`, { date: accessEndsDate })}
          description={translate(`${NOTICES_KEY}.pending_cancel.description`)}
          action={canRestore && actionButton(
            translate('subscription_management.active.renew_button'),
            { onClick: handleRenew, disabled: isRestoreLoading }
          )}
        />
      )
    case SUBSCRIPTION_STATUS.FULLY_CANCELED:
    case SUBSCRIPTION_STATUS.EXPIRED:
      if (canceledByAdmin && displayStatus === SUBSCRIPTION_STATUS.FULLY_CANCELED) return null

      return (
        <PlanNotice
          variant="neutral"
          title={translate(`${NOTICES_KEY}.ended.title`, { date: endedOn })}
          description={translate(`${NOTICES_KEY}.ended.description`)}
          action={canRestore && actionButton(
            translate('subscription_management.active.reactivate_button'),
            { onClick: handleReactivate, disabled: isRestoreLoading }
          )}
        />
      )
    default:
      return null
  }
}

StateNotice.propTypes = {
  displayStatus: string.isRequired,
  translate: func.isRequired,
  pausedUntilDate: string,
  pauseScheduledDate: string,
  pauseResumesDate: string,
  accessEndsDate: string,
  endedOn: string,
  suspendedDate: string,
  termPriceLabel: string.isRequired,
  updatePaymentUrl: string.isRequired,
  resumeInProgress: bool,
  isRetrySubmitted: bool,
  canResume: bool,
  isResumeLoading: bool,
  onResume: func,
  canCancelScheduledPause: bool,
  isCancelScheduledPauseLoading: bool,
  canRetry: bool,
  isRetryLoading: bool,
  onRetry: func,
  canceledByAdmin: bool,
  canRestore: bool,
  isRestoreLoading: bool,
  onRenew: func,
  onReactivate: func,
}

export default StateNotice
